import { CreateSubmission, ResponseField } from "types";
import { toast } from "react-toastify";
import { displayFields } from "../../../pages/AppPages/TherapistPages/AssignmentBuilder/components/AssignmentBuilderMenu/fieldTypes";
import { useCurrentUser } from "utils/hooks";

const usePublicSubmission = () => {
  const { user } = useCurrentUser();

  const createPublicSubmission = async <T>(
    submission: CreateSubmission,
    responseFields: ResponseField[],
    organizationId?: string
  ): Promise<T> => {
    const withNoId = responseFields.filter(
      (field) => !displayFields.includes(field.fieldType)
    );

    const args = submissionMutation(submission, withNoId, undefined);

    const serverCall = async () =>
      fetch(`${process.env.REACT_APP_REST_API_URL}/p/create-submission`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ args }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          organizationId: organizationId || user?.activeOrganizationId || "NA",
          permissionKey: "create_public_submissions",
        },
      })
        .then(async (res) => {
          if (res.status !== 200) {
            toast.error(`Hmm, something went wrong.`);
          }
          return await res.json();
        })
        .then((resData) => {
          if (resData.errors) {
            toast.error(`Hmm, something went wrong.`);
          } else {
            return resData;
          }
        });

    return await serverCall();
  };

  const updatePublicSubmission = async <T>(
    submission: CreateSubmission,
    responseFields: ResponseField[],
    organizationId?: string
  ): Promise<T> => {
    const args = submissionMutation(submission, undefined, responseFields);

    const serverCall = async () =>
      fetch(`${process.env.REACT_APP_REST_API_URL}/p/update-submission`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ args }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          organizationId: organizationId || user?.activeOrganizationId || "NA",
          permissionKey: "update_public_submissions",
        },
      })
        .then(async (res) => {
          if (res.status !== 200) {
            toast.error(`Hmm, something went wrong.`);
          }
          return await res.json();
        })
        .then((resData) => {
          if (resData.errors) {
            toast.error(`Hmm, something went wrong.`);
          } else {
            return resData;
          }
        });

    return await serverCall();
  };

  return {
    createPublicSubmission,
    updatePublicSubmission,
  };
};

function submissionMutation(
  submission: CreateSubmission & { uuid?: string },
  responseFields?: ResponseField[],
  responseUpdateFields?: ResponseField[]
) {
  const parseValue = (value: any) => {
    if (typeof value === "object") {
      return JSON.stringify(value);
    } else {
      return value;
    }
  };

  const fieldRql = (field: ResponseField) => {
    return {
      uuid: field.uuid || "",
      fieldId: field.fieldId,
      fieldLabel: field.fieldLabel,
      fieldType: field.fieldType,
      sectionId: field.sectionId,
      sectionLabel: field.sectionLabel,
      value: `${parseValue(field.value)}`,
    };
  };

  const noIds = responseFields?.map((field) => fieldRql(field));
  const withIds = responseUpdateFields?.map((field) => fieldRql(field));

  return {
    therapistId: submission.therapistId,
    submission: {
      uuid: submission.uuid ? submission.uuid : "",
      userId: submission.userId,
      authorId: submission.authorId,
      userName: submission.userName,
      userEmail: submission.userEmail,
      assignmentId: submission.assignmentId,
      assignmentLabel: submission.assignmentLabel,
      privacy: submission.privacy,
      groupPrivacy: submission.groupPrivacy,
      isDraft: submission.isDraft,
      isWideData: submission.isWideData,
      hideFromPatient: submission.hideFromPatient ? true : false,
    },
    responseFields: responseFields ? noIds : "",
    responseUpdateFields: responseUpdateFields ? withIds : "",
  };
}

export default usePublicSubmission;
